// Derivations over an `activities` row.
//
// The table itself only stores id / req / res / is_processed / track_id /
// stage / timestamps, and in practice `stage` is always RECEIVED and
// `is_processed` always false (the response logger writes `res` but never
// updates those two). Everything that actually helps diagnose a request is
// therefore derived here from the payloads and the timestamps.

import { TILE_ACCENT } from '../../../../components/utils/SummaryTiles';

// A request is called slow past this; the same number is sent to the backend
// as `slow_ms` so the chip filter and the column agree.
export const SLOW_THRESHOLD_MS = 5000;

export const OUTCOME = {
  ok: { key: "ok", label: "OK", accent: TILE_ACCENT.good },
  failed: { key: "failed", label: "FAILED", accent: TILE_ACCENT.bad },
  missing: { key: "missing", label: "NO RESPONSE", accent: TILE_ACCENT.warn },
  unknown: { key: "unknown", label: "UNREADABLE", accent: TILE_ACCENT.info },
};

// The logger stores whatever `res.send()` was handed, which is sometimes the
// body object and sometimes a JSON string of it. Unwrap until it stops being
// a JSON string so both shapes read the same.
export const parsePayload = (raw) => {
  if (raw === null || raw === undefined || raw === "") {
    return { value: null, pretty: "", empty: true };
  }

  let value = raw;
  for (let i = 0; i < 3; i++) {
    if (typeof value !== "string") break;
    try {
      value = JSON.parse(value);
    } catch (err) {
      break;
    }
  }

  const isObject = value !== null && typeof value === "object";

  return {
    value: isObject ? value : null,
    pretty: isObject ? JSON.stringify(value, null, 2) : String(raw),
    empty: false,
  };
};

// A 67KB response and an empty one look identical once truncated, so say what
// the body actually carries: the first collection found, and its length.
export const describeShape = (value, depth = 0) => {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) return `[${value.length}]`;
  if (typeof value !== "object") return null;
  if (depth > 3) return null;

  const keys = Object.keys(value);

  for (let i = 0; i < keys.length; i++) {
    const child = value[keys[i]];
    if (Array.isArray(child)) return `${keys[i]}[${child.length}]`;
  }

  for (let i = 0; i < keys.length; i++) {
    const nested = describeShape(value[keys[i]], depth + 1);
    if (nested && nested.indexOf("[") > -1) return nested;
  }

  if (keys.length === 0) return "{}";
  return `{${keys.length} field${keys.length === 1 ? "" : "s"}}`;
};

export const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const formatDuration = (ms) => {
  if (ms === null || ms === undefined) return null;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60000);
  return `${minutes}m ${Math.round((ms % 60000) / 1000)}s`;
};

export const latencyAccent = (ms) => {
  if (ms === null || ms === undefined) return TILE_ACCENT.neutral;
  if (ms < 1000) return TILE_ACCENT.good;
  if (ms < SLOW_THRESHOLD_MS) return TILE_ACCENT.warn;
  return TILE_ACCENT.bad;
};

// Errors arrive either as a string ("Request failed with status code 401") or
// as the array error_handler pushes (["INTERNAL_SERVER_ERROR"]).
const readError = (error) => {
  if (!error) return null;
  if (Array.isArray(error)) return error.filter(Boolean).join(", ") || null;
  if (typeof error === "object") return JSON.stringify(error);
  return String(error);
};

/**
 * Everything the table needs for one row, in one pass.
 */
export const readActivity = (row) => {
  const req = parsePayload(row.req);
  const res = parsePayload(row.res);

  const body = res.value || {};
  const error = readError(body.error);

  let outcome = OUTCOME.unknown;
  if (res.empty) outcome = OUTCOME.missing;
  else if (error) outcome = OUTCOME.failed;
  else if (res.value) outcome = OUTCOME.ok;

  // createdAt is written when the request arrives, updatedAt when the response
  // is saved — nothing else touches these rows, so the gap is the round trip.
  let latencyMs = null;
  if (row.createdAt && row.updatedAt) {
    const delta = new Date(row.updatedAt).getTime() - new Date(row.createdAt).getTime();
    if (!Number.isNaN(delta) && delta >= 0) latencyMs = delta;
  }

  return {
    outcome,
    error,
    latencyMs,
    message: body.message || null,
    shape: describeShape(body.data),
    reqSize: (row.req || "").length,
    resSize: (row.res || "").length,
    reqPretty: req.pretty,
    resPretty: res.pretty,
    reqEmpty: req.empty || row.req === "{}",
    resEmpty: res.empty,
  };
};
